/**
 * Physical Asset Audits (AST-23) — the real "audit" tab inside Asset > Administration > History &
 * Audit, plus the mobile audit checklist and scan flow.
 *
 * An audit RUN has a scope (a location and/or a category; neither = the caller's whole register),
 * dates and named auditors. Starting a run snapshots every in-scope, in-service asset as an audit
 * ITEM (expected location, custodian and condition at that moment) so the discrepancy report
 * compares what the register said when the audit began with what the auditor physically found —
 * later register edits never rewrite an audit. Each item is marked `found`, `missing` or
 * `condition_changed`; closing the run freezes it with a summary. A closed or cancelled run is
 * immutable.
 *
 * Its own router rather than more routes on `assetContract`, for the same reason
 * asset-history/asset-batch split off: a distinct path prefix and a distinct permission axis
 * (`asset:audit:*`), and `asset.contract.ts` is large and shared.
 *
 * Permissions are granular on purpose (product decision 2026-09-26): someone who can READ an audit
 * can never verify assets or close it.
 *   read    — list/view runs, items and discrepancies
 *   create  — start a run
 *   manage  — edit a run in progress (name, due date, auditors, notes) or cancel it
 *   verify  — mark one asset found / missing / condition changed (also the mobile scan)
 *   close   — close a run with its summary and export the formal discrepancy report
 * Which roles hold these keys is the Roles team's call; nothing here grants them.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { assetConditionSchema, assetSummaryPersonSchema } from './asset.contract.js';
import { assetCsvExportSchema } from './vendor.contract.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const ASSET_AUDIT_RUN_STATUSES = ['in_progress', 'closed', 'cancelled'] as const;
export const assetAuditRunStatusSchema = z.enum(ASSET_AUDIT_RUN_STATUSES);
export type AssetAuditRunStatus = z.infer<typeof assetAuditRunStatusSchema>;

export const ASSET_AUDIT_ITEM_RESULTS = ['pending', 'found', 'missing', 'condition_changed'] as const;
export const assetAuditItemResultSchema = z.enum(ASSET_AUDIT_ITEM_RESULTS);
export type AssetAuditItemResult = z.infer<typeof assetAuditItemResultSchema>;

/** What an auditor can record — `pending` is the un-verified state, never an input. */
export const assetAuditVerifyResultSchema = z.enum(['found', 'missing', 'condition_changed']);
export type AssetAuditVerifyResult = z.infer<typeof assetAuditVerifyResultSchema>;

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const assetAuditCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  found: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
  conditionChanged: z.number().int().nonnegative(),
});
export type AssetAuditCounts = z.infer<typeof assetAuditCountsSchema>;

export const assetAuditRunSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** Human-readable reference from DocumentCounter kind 'asset_audit', e.g. "AUD-2026-00003". */
  runCode: z.string(),
  name: z.string(),
  status: assetAuditRunStatusSchema,
  scopeLocationId: uuidSchema.nullable(),
  scopeLocationName: z.string().nullable(),
  scopeCategoryId: uuidSchema.nullable(),
  scopeCategoryName: z.string().nullable(),
  startDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  notes: z.string().nullable(),
  auditors: z.array(assetSummaryPersonSchema),
  /** Live tally for an in-progress run; the frozen summary for a closed one (same shape). */
  counts: assetAuditCountsSchema,
  createdBy: assetSummaryPersonSchema.nullable(),
  createdAt: instantSchema,
  closedAt: instantSchema.nullable(),
  closedBy: assetSummaryPersonSchema.nullable(),
  closureNote: z.string().nullable(),
  cancelledAt: instantSchema.nullable(),
  cancelReason: z.string().nullable(),
});
export type AssetAuditRun = z.infer<typeof assetAuditRunSchema>;

export const assetAuditItemSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  /** Null only if the asset was deleted from the register after the audit ran. */
  assetId: uuidSchema.nullable(),
  assetCode: z.string(),
  assetName: z.string(),
  categoryName: z.string().nullable(),
  /** Snapshot taken when the run started. */
  expectedLocationId: uuidSchema.nullable(),
  expectedLocationName: z.string().nullable(),
  expectedCustodian: assetSummaryPersonSchema.nullable(),
  expectedCondition: assetConditionSchema,
  result: assetAuditItemResultSchema,
  observedCondition: assetConditionSchema.nullable(),
  note: z.string().nullable(),
  verifiedBy: assetSummaryPersonSchema.nullable(),
  verifiedAt: instantSchema.nullable(),
  /** True for `missing` and `condition_changed` — the rows the discrepancy report lists. */
  isDiscrepancy: z.boolean(),
});
export type AssetAuditItem = z.infer<typeof assetAuditItemSchema>;

// ---------------------------------------------------------------------------
// Bodies / queries
// ---------------------------------------------------------------------------

export const createAssetAuditRunBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  scopeLocationId: uuidSchema.optional(),
  scopeCategoryId: uuidSchema.optional(),
  startDate: dateOnlySchema,
  dueDate: dateOnlySchema.optional(),
  notes: z.string().trim().max(2000).optional(),
  auditorPersonIds: z.array(uuidSchema).min(1).max(50),
});
export type CreateAssetAuditRunBody = z.infer<typeof createAssetAuditRunBodySchema>;

export const updateAssetAuditRunBodySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  dueDate: dateOnlySchema.nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  auditorPersonIds: z.array(uuidSchema).min(1).max(50).optional(),
});
export type UpdateAssetAuditRunBody = z.infer<typeof updateAssetAuditRunBodySchema>;

export const cancelAssetAuditRunBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type CancelAssetAuditRunBody = z.infer<typeof cancelAssetAuditRunBodySchema>;

export const verifyAssetAuditItemBodySchema = z
  .object({
    result: assetAuditVerifyResultSchema,
    /** Required when `result` is `condition_changed`. */
    observedCondition: assetConditionSchema.optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.result !== 'condition_changed' || v.observedCondition !== undefined, {
    message: 'observedCondition is required when the condition has changed',
    path: ['observedCondition'],
  });
export type VerifyAssetAuditItemBody = z.infer<typeof verifyAssetAuditItemBodySchema>;

/** Mobile scan: the decoded asset code of a label, marked `found` in this run. */
export const scanAssetAuditBodySchema = z.object({
  assetCode: z.string().trim().min(1).max(64),
  observedCondition: assetConditionSchema.optional(),
  note: z.string().trim().max(1000).optional(),
});
export type ScanAssetAuditBody = z.infer<typeof scanAssetAuditBodySchema>;

export const closeAssetAuditRunBodySchema = z.object({
  /**
   * When items are still pending the close is refused (409) unless this is true, in which case
   * every pending item is recorded as `missing` by the closer.
   */
  markUnverifiedAsMissing: z.boolean().default(false),
  note: z.string().trim().max(2000).optional(),
});
export type CloseAssetAuditRunBody = z.infer<typeof closeAssetAuditRunBodySchema>;

export const listAssetAuditRunsQuerySchema = paginationQuerySchema.extend({
  status: assetAuditRunStatusSchema.optional(),
  locationId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  /** Only runs the caller is a named auditor on (the mobile "my audits" list). */
  assignedToMe: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type ListAssetAuditRunsQuery = z.infer<typeof listAssetAuditRunsQuerySchema>;

export const listAssetAuditItemsQuerySchema = paginationQuerySchema.extend({
  result: assetAuditItemResultSchema.optional(),
  /** Only `missing` + `condition_changed` rows. */
  discrepanciesOnly: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  search: z.string().trim().max(100).optional(),
});
export type ListAssetAuditItemsQuery = z.infer<typeof listAssetAuditItemsQuerySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const runIdParams = z.object({ runId: uuidSchema });

export const assetAuditContract = c.router(
  {
    listAuditRuns: {
      method: 'GET',
      path: '/',
      query: listAssetAuditRunsQuerySchema,
      summary: 'List physical audit runs (asset:audit:read)',
      metadata: routeMeta('asset:audit:read'),
      responses: {
        200: paginatedSchema(assetAuditRunSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    createAuditRun: {
      method: 'POST',
      path: '/',
      body: createAssetAuditRunBodySchema,
      summary:
        'Start a physical audit run: snapshots every in-scope, in-service asset as a pending item (asset:audit:create)',
      metadata: routeMeta('asset:audit:create'),
      responses: {
        201: assetAuditRunSchema,
        400: errorBodySchema,
        422: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    getAuditRun: {
      method: 'GET',
      path: '/:runId',
      pathParams: runIdParams,
      summary: 'Get one audit run with its live tally (asset:audit:read)',
      metadata: routeMeta('asset:audit:read'),
      responses: {
        200: assetAuditRunSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    updateAuditRun: {
      method: 'PATCH',
      path: '/:runId',
      pathParams: runIdParams,
      body: updateAssetAuditRunBodySchema,
      summary: 'Edit an in-progress audit run — name, due date, notes, auditors (asset:audit:manage)',
      metadata: routeMeta('asset:audit:manage'),
      responses: {
        200: assetAuditRunSchema,
        400: errorBodySchema,
        422: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    cancelAuditRun: {
      method: 'POST',
      path: '/:runId/cancel',
      pathParams: runIdParams,
      body: cancelAssetAuditRunBodySchema,
      summary: 'Cancel an in-progress audit run (asset:audit:manage)',
      metadata: routeMeta('asset:audit:manage'),
      responses: {
        200: assetAuditRunSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    listAuditItems: {
      method: 'GET',
      path: '/:runId/items',
      pathParams: runIdParams,
      query: listAssetAuditItemsQuerySchema,
      summary: "List a run's per-asset checklist / discrepancies (asset:audit:read)",
      metadata: routeMeta('asset:audit:read'),
      responses: {
        200: paginatedSchema(assetAuditItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    verifyAuditItem: {
      method: 'POST',
      path: '/:runId/items/:itemId/verify',
      pathParams: z.object({ runId: uuidSchema, itemId: uuidSchema }),
      body: verifyAssetAuditItemBodySchema,
      summary:
        'Mark one asset found / missing / condition changed in an in-progress run (asset:audit:verify)',
      metadata: routeMeta('asset:audit:verify'),
      responses: {
        200: assetAuditItemSchema,
        400: errorBodySchema,
        422: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    scanAuditAsset: {
      method: 'POST',
      path: '/:runId/scan',
      pathParams: runIdParams,
      body: scanAssetAuditBodySchema,
      summary:
        'Mark the asset with this scanned code as found in an in-progress run (asset:audit:verify)',
      metadata: routeMeta('asset:audit:verify'),
      responses: {
        200: assetAuditItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    closeAuditRun: {
      method: 'POST',
      path: '/:runId/close',
      pathParams: runIdParams,
      body: closeAssetAuditRunBodySchema,
      summary: 'Close an audit run and freeze its summary (asset:audit:close)',
      metadata: routeMeta('asset:audit:close'),
      responses: {
        200: assetAuditRunSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    exportAuditReport: {
      method: 'GET',
      path: '/:runId/report',
      pathParams: runIdParams,
      summary: "Export the run's discrepancy report as CSV (asset:audit:close + asset:audit:read)",
      metadata: routeMeta(['asset:audit:close', 'asset:audit:read']),
      responses: {
        200: assetCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
  },
  { pathPrefix: '/asset-management/audits' },
);
